import React, {useState, useEffect} from 'react';
import {Link, Navigate, useNavigate} from 'react-router-dom';
import {useAuth} from "../config/authContext";
import {doCreateUserWithEmailAndPassword, doSendEmailVerification} from "../config/authUser";
import Logotipo from "../assets/Icons/Logotipo.svg";
import 'primeicons/primeicons.css';
import "../styles/Auth.css";
import { auth } from "../config/firebase";

const Register = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isRegistering, setIsRegistering] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { userLoggedIn } = useAuth();

    useEffect(() => {
        const preRegisterEmail = localStorage.getItem('preRegisterEmail');
        if (preRegisterEmail) {
            setEmail(preRegisterEmail);
            localStorage.removeItem('preRegisterEmail');
        }
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!isRegistering) {
            setIsRegistering(true);
            try {
                if (password !== confirmPassword) {
                    setErrorMessage('Passwords do not match');
                    setIsRegistering(false);
                    return;
                }
                const userCredential = await doCreateUserWithEmailAndPassword(email, password);
                // Mark this session as a fresh signup via email so onboarding triggers once
                localStorage.setItem('loginMethod', 'email');
                localStorage.setItem('NewUser', JSON.stringify({ loginMethod: 'email', email: userCredential.user?.email || email }));
                // Send verification email and route to verify screen
                try {
                    await doSendEmailVerification(userCredential.user);
                    console.log('Verification email sent after signup');
                } catch (e) {
                    console.error('Failed to send verification email after signup', e);
                }
                navigate('/verify-email');
            } catch (error) {
                setErrorMessage('Failed to register');
                setIsRegistering(false);
            }
        }
    };

    // Only send to questionnaire if there's a pending NewUser flag for this logged-in user
    const isPendingNewUser = (() => {
        try {
            const raw = localStorage.getItem('NewUser');
            if (!raw) return false;
            const data = JSON.parse(raw);
            const currentEmail = auth.currentUser?.email;
            if (!currentEmail) return true; // allow immediate post-signup redirect
            return data?.email && data.email === currentEmail;
        } catch (_) {
            return false;
        }
    })();

    return (
        <div className="home">
            {userLoggedIn && (
                isPendingNewUser ? (
                    <Navigate to={'/questionnaire'} replace={true} />
                ) : (
                    <Navigate to={'/dashboard'} replace={true} />
                )
            )}

            {/* Navigation Bar */}
            <header className="navigation-presite-bar">
                <div className="logo-wrapper">
                    <div className="logo">
                        Frazza
                    </div>
                </div>
            </header>

            <div className="auth-layout">
                {/* Left Side - Auth Form */}
                <div className="auth-left-container">
                    <div className="sub-logo">
                        <h1 className="main-heading">
                            Because Distance Shouldn't Mean Disconnection
                        </h1>
                        <p>Hello there! Create a new account</p>
                    </div>

                    <div className="auth-container">
                        <div className="auth-box">
                            <form onSubmit={onSubmit} className="auth-form frazza-form">
                                <div className="frazza-form-group">
                                    <label className="frazza-form-label required">Email</label>
                                    <input
                                        type="email"
                                        className="frazza-input"
                                        autoComplete="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="your@email.com"
                                    />
                                </div>
                                <div className="frazza-form-group">
                                    <label className="frazza-form-label required">Password</label>
                                    <input
                                        disabled={isRegistering}
                                        type="password"
                                        className="frazza-input"
                                        autoComplete="new-password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Create a strong password"
                                    />
                                    <span className="frazza-form-help">Use at least 8 characters with a mix of letters and numbers</span>
                                </div>
                                <div className="frazza-form-group">
                                    <label className="frazza-form-label required">Confirm Password</label>
                                    <input
                                        disabled={isRegistering}
                                        type="password"
                                        className="frazza-input"
                                        autoComplete="off"
                                        required
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Re-enter your password"
                                    />
                                </div>
                                {errorMessage && (
                                    <div className="frazza-form-error">{errorMessage}</div>
                                )}
                                <button 
                                    type="submit" 
                                    disabled={isRegistering} 
                                    className={`frazza-btn frazza-btn-primary frazza-btn-block ${isRegistering ? 'loading' : ''}`}
                                >
                                    {isRegistering ? 'Signing Up...' : 'Sign Up'}
                                </button>
                            </form>
                            <p className="auth-footer register-footer">Already have an account? <Link to={'/auth'}>Sign in</Link></p>
                        </div>
                    </div>
                </div>

                {/* Right Side - Logo Container */}
                <div className="logo-container">
                    <img src={Logotipo} alt="Logotipo de personas" className="logo-icon4" />
                </div>
            </div>
        </div>
    );
};

export default Register;